import { useState } from 'react'
import { BaseEmoji, DocHandbookItem } from 'types'
import { MentionIcon } from 'components/MentionIcon/MentionIcon'
import { handbookLink } from '../../util'
import { useHandbookItems } from '../HandbookItemProvider'
import Styles from '../HandbookItems.module.scss'
import { Icon } from './Components/Icon'
import DocTitleInput from './DocTitleInput'
import { NavItem } from 'components/NavItem'
import { MenuType } from 'components/Handbook2_0/ContextMenus/Types'
import { observer } from 'mobx-react-lite'
import QueryString from 'qs'
import { useQueryParams } from 'hooks/useQueryParams'
import { useHandbookPermissions } from 'components/Handbook2_0/hooks'
import { useRootStore } from 'stores/RootStore'
import { MentionItemMenu } from 'components/Handbook2_0/ContextMenus/ItemMenu/MentionItemMenu'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { ContextMenuTrigger } from 'almanac-ui/ContextMenuTrigger/ContextMenuTrigger'
import { DocMenuContainer } from 'components/DocMenu/DocMenuContainer'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { MoreThick } from 'almanac-ui/icons'
import { DocItemMenu } from 'components/Handbook2_0/ContextMenus/ItemMenu/DocItemMenu'
import { isUndefined } from 'lodash'
import { ChannelType } from 'components/CampaignCreationModal/Types'

interface Props {
  item: DocHandbookItem
  depth: number
  showOptions?: boolean
  isSelected?: boolean
  isLink?: boolean
}

const Doc = observer(({ item, depth = 0, showOptions = true, isSelected, isLink }: Props) => {
  const { currentHandbook: handbook, treeStore } = useHandbookItems()
  const { handbooks, docStore } = useRootStore()
  const treeItem = handbooks.items.get(item.id.toString())
  const { canEdit } = useHandbookPermissions(handbook)
  const _isCoverDocItem = treeStore?.tree && handbook && treeStore.isCoverDocItem(handbook, item)
  const { focusView } = useQueryParams()
  const [isRenaming, setIsRenaming] = useState(false)

  if (!handbook || !item.document || !treeItem || !treeItem.data.document) return null

  const {
    data: { document, mention_id },
    updateAttrs,
  } = treeItem
  const isMention = Boolean(mention_id)

  const linkTo = {
    pathname: handbookLink(handbook, document.seo_string, !!handbook.folder),
    search: QueryString.stringify(
      {
        focusView,
        mention_id: item.mention_id,
      },
      { filter: (_, value) => value ?? undefined },
    ),
  }

  const updateEmoji = async (newEmoji: BaseEmoji | null) => {
    if (!document) return

    const header_emoji = newEmoji?.native || null
    const channel = newEmoji?.native === undefined ? newEmoji?.id : null
    updateAttrs({ document: { ...document, header_emoji, channel_emoji: channel as ChannelType } })
  }

  const onTemplateChange = async () => {
    const newAlmanacFile = { ...document.almanac_file, is_template: !document.almanac_file?.is_template }
    updateAttrs({ document: { ...document, almanac_file: newAlmanacFile } })
  }

  if (isRenaming) {
    return (
      <div className={Styles.renameWrapper}>
        <DocTitleInput
          treeItem={treeItem}
          item={item}
          onCancel={() => setIsRenaming(false)}
          onRename={() => setIsRenaming(false)}
        />
      </div>
    )
  }

  const docMenuPopover = (close: () => void) => (
    <DocMenuContainer
      id={item.document.key}
      hiddenKeys={new Set(['toggleVersionControl', 'switchView', 'cleanup', 'readHistory', 'export', 'readInsights'])}
      dismiss={close}
      onAction={(key) => {
        if (key === 'rename') {
          setIsRenaming(true)
        }
        close()
      }}
    />
  )

  const LegacyMenu = isMention ? MentionItemMenu : DocItemMenu

  return (
    <ConditionalWrapper
      condition={isMention}
      wrapper={(children) => {
        return (
          <LegacyMenu
            item={item}
            currentUserCanAccess={canEdit}
            type={MenuType.contextMenu}
            onEdit={() => setIsRenaming(true)}
            onTemplateChange={onTemplateChange}
          >
            {children}
          </LegacyMenu>
        )
      }}
      elseWrapper={(children) => (
        <ConditionalWrapper
          condition={!!showOptions}
          wrapper={(children) => {
            return (
              <ContextMenuTrigger placement="bottom-start">
                {children}
                {docMenuPopover}
              </ContextMenuTrigger>
            )
          }}
        >
          {children}
        </ConditionalWrapper>
      )}
    >
      <div>
        <NavItem.Root
          to={isLink ? linkTo : undefined}
          isActive={isUndefined(isSelected) ? docStore?.articleId === item.document.key : isSelected}
          indentLevel={depth}
        >
          <NavItem.Emoji
            isEditable={showOptions && canEdit}
            onChange={updateEmoji}
            fileKey={item.document.key}
            withChannels
          >
            <Icon item={item} />
            {isMention && canEdit && <MentionIcon />}
          </NavItem.Emoji>
          <NavItem.Label label={item.title || ''} />
          {showOptions && (
            <NavItem.ControlGroup>
              {isMention ? (
                <LegacyMenu
                  item={item}
                  type={MenuType.popover}
                  currentUserCanAccess={!_isCoverDocItem && canEdit}
                  onEdit={() => setIsRenaming(true)}
                  children={undefined}
                />
              ) : (
                <DropdownTrigger>
                  <NavItem.Control aria-label="Options" icon={MoreThick} />
                  {docMenuPopover}
                </DropdownTrigger>
              )}
            </NavItem.ControlGroup>
          )}
        </NavItem.Root>
      </div>
    </ConditionalWrapper>
  )
})

const DocContainer = (props: Props) => {
  if (!props.item.document) return null

  return <Doc {...props} />
}

export { DocContainer as Doc }
